import { useEffect, useState } from 'react';
import { supabase } from '../lib/db';
import { logger } from '../services/logger';
import { Trophy, Medal, Award, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePromoConfig } from '../hooks/usePromoConfig';
import { useAuth } from '../lib/auth';
import { ClickableUserName } from './common/ClickableUserName';
import { colors, ACCENT_COLOR } from '../config/brand';

// Inline gradient style for leaderboard header (ensures colors work regardless of Tailwind build)
const leaderboardHeaderStyle = {
  background: `linear-gradient(to right, ${colors.primary.teal}, ${colors.primary.darkTeal}, ${colors.secondary.slate})`,
};

interface LeaderboardEntry {
  rank_position: number;
  user_id: string;
  user_email: string;
  user_name: string;
  current_balance: number;
  starting_balance: number;
  active_bets_value: number;
  total_value: number;
  profit: number;
  affiliate_earnings: number;
  trading_profit: number;
  total_bets: number;
  active_bets: number;
  winning_bets: number;
  win_rate: number;
}

interface LeaderboardProps {
  limit?: number;
  showDetails?: boolean;
}

export function Leaderboard({ limit = 10, showDetails = true }: LeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPosition, setUserPosition] = useState<number | null>(null);
  const { promoConfig } = usePromoConfig();
  const { user } = useAuth();
  
  // Check if current user is an admin
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  useEffect(() => {
    fetchLeaderboard();
    if (user) {
      fetchUserPosition();
    }
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchLeaderboard();
      if (user) fetchUserPosition();
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, user]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('get_promo_leaderboard', {
        p_limit: limit,
        p_start_date: null, // Will use promo config dates
        p_end_date: null    // Will use promo config dates
      });

      if (rpcError) throw rpcError;
      setEntries(data || []);
    } catch (err) {
      logger.error('Error fetching leaderboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosition = async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_user_competition_positions', {
        p_user_id: user?.id,
        p_competition_id: null
      });

      if (rpcError) throw rpcError;
      if (data && data.length > 0) {
        setUserPosition(data[0].profit_position);
      }
    } catch (err) {
      logger.error('Error fetching user position:', err);
    }
  };

  const formatCurrency = (amount: number): string => {
    return `R${amount.toFixed(2)}`;
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="relative">
            <Trophy className="w-6 h-6 text-yellow-500 drop-shadow-lg" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
          </div>
        );
      case 2:
        return (
          <div className="relative">
            <Medal className="w-6 h-6 text-gray-400 drop-shadow-md" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-300 rounded-full"></div>
          </div>
        );
      case 3:
        return (
          <div className="relative">
            <Award className="w-6 h-6 text-amber-600 drop-shadow-md" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full"></div>
          </div>
        );
      default:
        return null; // Don't show icon for ranks 4+, just use the # display
    }
  };

  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs font-bold shadow-lg">
            <Trophy className="w-3.5 h-3.5" />
            <span>1st Place</span>
          </div>
        );
      case 2:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-gray-300 to-gray-400 text-white text-xs font-bold shadow-md">
            <Medal className="w-3.5 h-3.5" />
            <span>2nd Place</span>
          </div>
        );
      case 3:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold shadow-md">
            <Award className="w-3.5 h-3.5" />
            <span>3rd Place</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getRankBgColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="text-white p-4" style={leaderboardHeaderStyle}>
          <div className="flex items-center space-x-2">
            <Trophy className="w-6 h-6" />
            <h2 className="text-xl font-bold">Top {limit} Winners</h2>
          </div>
        </div>
        <div className="flex items-center justify-center h-64 p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ACCENT_COLOR }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center text-red-600">
          <p className="text-lg font-medium">Error Loading Leaderboard</p>
          <p className="text-sm mt-2">{error}</p>
          <button
            type="button"
            onClick={fetchLeaderboard}
            className="mt-4 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
            style={{ backgroundColor: ACCENT_COLOR }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center text-gray-500">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No Leaderboard Data Yet</p>
          <p className="text-sm mt-2">Start trading to see top performers!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header - Clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-white p-4 text-left hover:opacity-90 transition-opacity"
        style={leaderboardHeaderStyle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Trophy className="w-6 h-6" />
            <h2 className="text-xl font-bold">Top {limit} Winners</h2>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-sm font-medium">
              {promoConfig?.promo_name || 'Leaderboard'}
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
        <p className="text-sm mt-2 opacity-90">
          {isAdmin 
            ? 'Winners ranked by profit (Current Balance + Trades Value - Starting Balance)'
            : 'Winners ranked by profit percentage'}
        </p>
      </button>

      {/* User's Own Position Card - Always visible when logged in */}
      {user && userPosition && !isAdmin && (
        <div className={`px-4 py-3 border-b ${
          userPosition === 1 ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200' :
          userPosition === 2 ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200' :
          userPosition === 3 ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' :
          'bg-blue-50 border-blue-100'
        }`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              userPosition <= 3 ? 'text-gray-700' : 'text-blue-700'
            }`}>
              Your Position:
            </span>
            <div className="flex items-center space-x-2">
              {userPosition <= 3 && getRankIcon(userPosition)}
              <span className={`text-lg font-bold ${
                userPosition === 1 ? 'text-yellow-700' :
                userPosition === 2 ? 'text-gray-700' :
                userPosition === 3 ? 'text-amber-700' :
                'text-blue-700'
              }`}>
                #{userPosition}
              </span>
              {userPosition <= 3 && (
                <div className="ml-1">
                  {getRankBadge(userPosition)}
                </div>
              )}
              {userPosition > 3 && userPosition <= 10 && (
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                  Top 10! 🎉
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collapsible Content */}
      {isExpanded && (
        <>

      {/* Leaderboard Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trader
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {isAdmin ? 'Profit' : '% Profit'}
              </th>
              {/* Only show these columns for admins */}
              {isAdmin && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                  {showDetails && (
                    <>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active Trades
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Win Rate
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Trades
                      </th>
                    </>
                  )}
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {entries.map((entry) => (
              <tr
                key={entry.user_id}
                className={`hover:bg-gray-50 transition-colors ${getRankBgColor(entry.rank_position)}`}
              >
                {/* Rank */}
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex flex-col items-start space-y-1">
                    <div className="flex items-center space-x-2">
                      {getRankIcon(entry.rank_position)}
                      <span className="text-sm font-bold text-gray-700">
                        #{entry.rank_position}
                      </span>
                    </div>
                    {entry.rank_position <= 3 && getRankBadge(entry.rank_position)}
                  </div>
                </td>

                {/* Trader Name */}
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ background: `linear-gradient(to bottom right, ${colors.primary.teal}, ${colors.primary.darkTeal})` }}
                    >
                      {entry.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {isAdmin ? (
                          <ClickableUserName userId={entry.user_id} displayName={entry.user_name} />
                        ) : (
                          /* Main dashboard: show first name only, hide last name and email */
                          entry.user_name.trim().split(/\s+/)[0] || entry.user_name
                        )}
                      </div>
                      {showDetails && isAdmin && (
                        <div className="text-xs text-gray-500">
                          {entry.user_email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Profit - Show only % for regular users, full details for admins */}
                <td className="px-4 py-4 whitespace-nowrap text-right">
                  {isAdmin ? (
                    <>
                      <div className={`text-sm font-bold ${
                        entry.profit >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {entry.profit >= 0 ? '+' : ''}{formatCurrency(entry.profit)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {((entry.profit / entry.starting_balance) * 100).toFixed(1)}%
                      </div>
                      {/* Profit breakdown for admins */}
                      <div className="text-xs mt-1 space-y-0.5">
                        {entry.trading_profit !== 0 && (
                          <div className={entry.trading_profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                            Trading: {entry.trading_profit >= 0 ? '+' : ''}{formatCurrency(entry.trading_profit)}
                          </div>
                        )}
                        {entry.affiliate_earnings > 0 && (
                          <div className="text-purple-600">
                            Affiliates: +{formatCurrency(entry.affiliate_earnings)}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className={`text-sm font-bold ${
                      entry.profit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {entry.profit >= 0 ? '+' : ''}{((entry.profit / entry.starting_balance) * 100).toFixed(1)}%
                    </div>
                  )}
                </td>

                {/* Total Value - Only for admins */}
                {isAdmin && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(entry.total_value)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                      <div className="flex items-center justify-end">
                        <span>Balance:</span>
                        <span className="ml-1">{formatCurrency(entry.current_balance)}</span>
                      </div>
                      {entry.active_bets_value > 0 && (
                        <div className="flex items-center justify-end">
                          <span className="text-blue-600">In Trades:</span>
                          <span className="ml-1 text-blue-600">{formatCurrency(entry.active_bets_value)}</span>
                        </div>
                      )}
                    </div>
                  </td>
                )}

                {/* Trades (if showDetails and admin) */}
                {showDetails && isAdmin && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900">
                      {entry.active_bets}
                    </div>
                    {entry.active_bets_value > 0 && (
                      <div className="text-xs text-blue-600">
                        {formatCurrency(entry.active_bets_value)} locked
                      </div>
                    )}
                  </td>
                )}

                {/* Win Rate (if showDetails and admin) */}
                {showDetails && isAdmin && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {entry.win_rate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry.winning_bets}/{entry.total_bets} wins
                    </div>
                  </td>
                )}

                {/* Total Trades (if showDetails and admin) */}
                {showDetails && isAdmin && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900">
                      {entry.total_bets}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-gray-500">
                Updated every 30 seconds
                {isAdmin && (
                  <>
                    {' • '}Profit = Total Value - Starting Balance
                    {promoConfig?.starting_balance && (
                      <span> • Starting Balance: {formatCurrency(promoConfig.starting_balance)}</span>
                    )}
                  </>
                )}
              </p>
              {promoConfig?.competition_terms_content && (
                <Link 
                  to="/competition-terms" 
                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                >
                  <FileText className="w-3 h-3" />
                  View T&Cs
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Leaderboard;

